// ============================================================
// DASHBOARD HOME — pagina principale area utente
// ============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { courseService } from '../../../services';
import { notificationService } from '../../../services';
import type { Course, Notification, Progress } from '../../../types/course';
import type { WeeklyStats } from '../../../types/stats';
import './DashboardHome.css';
import { formatDuration } from '../../../utils/formatDuration';
import { useEmailVerifiedToast } from '../../../hooks/useEmailVerifiedToast';

// ─── Helpers ─────────────────────────────────────────────────

const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

// Converte una stringa 'YYYY-MM-DD' nell'indice del giorno della settimana
// con lunedì = 0 (coerente con l'array DAYS)
const dateToDayIndex = (dateStr: string): number =>
  (new Date(dateStr + 'T00:00:00').getDay() + 6) % 7;

interface StatCardProps {
  icon: string;
  value: string;
  label: string;
  accent?: boolean;
  delay?: number;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  value,
  label,
  accent,
  delay = 0,
}) => (
  <div
    className={`dash-stat-card ${accent ? 'dash-stat-card--accent' : ''}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="dash-stat-icon">
      <i className={`bi ${icon}`} />
    </div>
    <div className="dash-stat-value">{value}</div>
    <div className="dash-stat-label">{label}</div>
  </div>
);

interface WeeklyBarProps {
  day: string;
  minutes: number;
  maxMinutes: number;
  isToday: boolean;
}

const WeeklyBar: React.FC<WeeklyBarProps> = ({
  day,
  minutes,
  maxMinutes,
  isToday,
}) => {
  const height =
    maxMinutes > 0
      ? Math.max((minutes / maxMinutes) * 100, minutes > 0 ? 8 : 0)
      : 0;
  return (
    <div className="dash-weekly-bar-col">
      <div className="dash-weekly-bar-wrap">
        <div
          className={`dash-weekly-bar ${isToday ? 'dash-weekly-bar--today' : ''} ${minutes > 0 ? 'dash-weekly-bar--active' : ''}`}
          style={{ height: `${height}%` }}
          title={minutes > 0 ? `${minutes} min` : 'Nessuna attività'}
        />
      </div>
      <span
        className={`dash-weekly-day ${isToday ? 'dash-weekly-day--today' : ''}`}
      >
        {day}
      </span>
      {minutes > 0 && <span className="dash-weekly-minutes">{minutes}'</span>}
    </div>
  );
};

const DashboardHome: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [courses, setCourses] = useState<Course[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [progressMap, setProgressMap] = useState<
    Record<string, Progress | null>
  >({});
  const [weeklyStats, setWeeklyStats] = useState<WeeklyStats>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const verifiedStatus = useEmailVerifiedToast();

  useEffect(() => {
    const load = async () => {
      try {
        const [fetchedCourses, fetchedNotifications, fetchedWeekly] =
          await Promise.all([
            courseService.getMyCourses(),
            notificationService.getAll(),
            courseService.getWeeklyStats(),
          ]);
        setCourses(fetchedCourses);
        setNotifications(fetchedNotifications);
        setWeeklyStats(fetchedWeekly);

        const progressEntries = await Promise.all(
          fetchedCourses.map(async (c) => {
            const p = await courseService.getProgress(c.id);
            return [c.id, p] as [string, Progress | null];
          })
        );
        setProgressMap(Object.fromEntries(progressEntries));
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const totalWatchedSeconds = useMemo(
    () =>
      Object.values(progressMap).reduce(
        (tot, p) => tot + (p?.secondsWatched ?? 0),
        0
      ),
    [progressMap]
  );

  const completedLessonsCount = useMemo(
    () =>
      Object.values(progressMap).reduce(
        (tot, p) => tot + (p?.completedLessonIds?.length ?? 0),
        0
      ),
    [progressMap]
  );

  const totalFormatted = useMemo(() => {
    const h = Math.floor(totalWatchedSeconds / 3600);
    const m = Math.floor((totalWatchedSeconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m} min`;
  }, [totalWatchedSeconds]);

  const resumeData = useMemo(() => {
    for (const course of courses) {
      const p = progressMap[course.id];
      if (p?.lessonId) {
        const lesson = course.lessons.find((l) => l.id === p.lessonId);
        if (lesson) return { course, lesson, secondsWatched: p.secondsWatched };
      }
    }
    return null;
  }, [courses, progressMap]);

  // ── Grafico settimanale — dati reali da API ───────────────
  // weeklyStats è un array di 7 elementi [{date, secondsWatched}]
  // ordinati dal più vecchio al più recente (backend garantisce l'ordine).
  const weeklyData = useMemo(() => {
    if (weeklyStats.length === 0) {
      // Fallback: 7 giorni a zero mentre carica
      const today = new Date();
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() - (6 - i));
        return {
          day: DAYS[dateToDayIndex(d.toISOString().slice(0, 10))],
          minutes: 0,
        };
      });
    }
    return weeklyStats.map((s) => ({
      day: DAYS[dateToDayIndex(s.date)],
      minutes: Math.floor(s.secondsWatched / 60),
    }));
  }, [weeklyStats]);

  const maxMinutes = Math.max(...weeklyData.map((d) => d.minutes), 1);
  const weeklyTotal = weeklyData.reduce((s, d) => s + d.minutes, 0);

  const calcCoursePercent = (course: Course): number => {
    const p = progressMap[course.id];
    if (!p || course.lessons?.length === 0) return 0;
    return Math.round(
      ((p.completedLessonIds?.length ?? 0) / course.lessons.length) * 100
    );
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center gap-3 text-center"
        style={{ minHeight: '60vh' }}
        role="alert"
      >
        <i className="bi bi-wifi-off fs-1 text-body-secondary" />
        <p className="fw-semibold mb-0">Impossibile caricare la dashboard</p>
        <p className="text-body-secondary small mb-0">
          Controlla la connessione e riprova.
        </p>
        <button
          className="btn btn-primary btn-sm mt-1"
          onClick={() => window.location.reload()}
        >
          Ricarica pagina
        </button>
      </div>
    );
  }

  return (
    <div className="dash-home">
      {/* ── Benvenuto ── */}
      <section className="dash-welcome">
        <div className="dash-welcome-text">
          <h1 className="dash-welcome-title">
            Bentornato/a,{' '}
            <span className="dash-welcome-name">{user?.firstName}</span>! 👋
          </h1>
          <p className="dash-welcome-sub">
            Continua il tuo percorso di benessere.
          </p>
        </div>

        {/* Notifiche non lette */}
        {unreadCount > 0 && (
          <button
            className="dash-notif-chip"
            onClick={() => navigate('/dashboard/notifiche')}
          >
            <i className="bi bi-bell-fill me-1" />
            {unreadCount} nuov{unreadCount === 1 ? 'a' : 'e'} notific
            {unreadCount === 1 ? 'a' : 'he'}
          </button>
        )}
      </section>

      {verifiedStatus === 'verified' && (
        <div
          className="alert alert-success d-flex align-items-center gap-2 mb-0"
          role="alert"
        >
          <i className="bi bi-check-circle-fill flex-shrink-0" />
          <span>
            Email verificata con successo! Il tuo account è ora attivo.
          </span>
        </div>
      )}
      {verifiedStatus === 'already' && (
        <div
          className="alert alert-info d-flex align-items-center gap-2 mb-0"
          role="alert"
        >
          <i className="bi bi-info-circle-fill flex-shrink-0" />
          <span>La tua email era già stata verificata.</span>
        </div>
      )}

      {/* ── Riprendi dall'ultimo punto ── */}
      {resumeData && (
        <section className="dash-resume">
          <div className="dash-resume-info">
            <span className="dash-resume-tag">
              <i className="bi bi-play-circle-fill me-1" />
              Riprendi
            </span>
            <p className="dash-resume-course">{resumeData.course.title}</p>
            <p className="dash-resume-lesson">{resumeData.lesson.title}</p>
            <p className="dash-resume-time">
              {resumeData.lesson.type === 'audio' ? (
                <i className="bi bi-music-note me-1" />
              ) : (
                <i className="bi bi-camera-video me-1" />
              )}
              Fermo a {formatDuration(resumeData.secondsWatched)} su{' '}
              {formatDuration(resumeData.lesson.durationSeconds)}
            </p>
          </div>
          <button
            className="dash-resume-btn"
            onClick={() =>
              navigate(
                `/dashboard/corsi/${resumeData.course.id}?lesson=${resumeData.lesson.id}`
              )
            }
          >
            <i className="bi bi-play-fill me-1" />
            Riprendi
          </button>
        </section>
      )}

      {/* ── Stat cards ── */}
      <section className="dash-stats">
        <StatCard
          icon="bi-clock"
          value={totalFormatted}
          label="Contenuti visti"
          delay={0}
        />
        <StatCard
          icon="bi-check2-circle"
          value={String(completedLessonsCount)}
          label={
            completedLessonsCount === 1
              ? 'Lezione completata'
              : 'Lezioni completate'
          }
          delay={80}
        />
        <StatCard
          icon="bi-collection-play"
          value={String(courses.length)}
          label={courses.length === 1 ? 'Corso acquistato' : 'Corsi acquistati'}
          delay={160}
        />
      </section>

      {/* ── Grafico settimanale ── */}
      <section className="dash-card dash-weekly">
        <div className="dash-card-header">
          <h2 className="dash-card-title">
            <i className="bi bi-bar-chart me-2" />
            La tua settimana
          </h2>
          <span className="dash-weekly-total">
            {weeklyTotal > 0 ? `${weeklyTotal} min totali` : 'Nessuna attività'}
          </span>
        </div>
        <div className="dash-weekly-chart">
          {weeklyData.map((d, i) => (
            <WeeklyBar
              key={d.day + i}
              day={d.day}
              minutes={d.minutes}
              maxMinutes={maxMinutes}
              isToday={i === 6} // l'ultimo elemento è sempre oggi (backend ordina dal più vecchio)
            />
          ))}
        </div>
      </section>

      {/* ── I miei corsi ── */}
      <section className="dash-card">
        <div className="dash-card-header">
          <h2 className="dash-card-title">
            <i className="bi bi-collection-play me-2" />I miei corsi
          </h2>
          <button
            className="dash-card-link"
            onClick={() => navigate('/dashboard/corsi')}
          >
            Vedi tutti
            <i className="bi bi-arrow-right ms-1" />
          </button>
        </div>

        {courses.length === 0 ? (
          <div className="dash-empty">
            <i className="bi bi-cart-plus dash-empty-icon" />
            <p className="dash-empty-text">
              Non hai ancora acquistato nessun corso.
            </p>
            <button
              className="dash-empty-cta"
              onClick={() => navigate('/rimettiti-al-centro')}
            >
              Scopri i corsi
            </button>
          </div>
        ) : (
          <div className="dash-courses-list">
            {courses.map((course) => {
              const percent = calcCoursePercent(course);
              const p = progressMap[course.id];
              const lastLesson = p?.lessonId
                ? course.lessons.find((l) => l.id === p.lessonId)
                : course.lessons[0];

              return (
                <div
                  key={course.id}
                  className="dash-course-item"
                  onClick={() => navigate(`/dashboard/corsi/${course.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    navigate(`/dashboard/corsi/${course.id}`)
                  }
                >
                  {/* Thumb */}
                  <div className="dash-course-thumb">
                    {course.imageUrl ? (
                      <img src={course.imageUrl} alt={course.title} />
                    ) : (
                      <i className="bi bi-play-circle" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="dash-course-info">
                    <p className="dash-course-title">{course.title}</p>
                    <p className="dash-course-sub">
                      {course.lessons.length} lezioni
                      {p
                        ? ` · ultima: ${lastLesson?.title ?? '—'}`
                        : ' · non ancora iniziato'}
                    </p>

                    {/* Progress bar */}
                    <div className="dash-course-progress-wrap">
                      <div className="dash-course-progress-bar">
                        <div
                          className="dash-course-progress-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="dash-course-percent">{percent}%</span>
                    </div>
                  </div>
                  <i className="bi bi-chevron-right dash-course-arrow" />
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default DashboardHome;
